import { IMsalContext, useMsal } from '@azure/msal-react';
import { 
  Alert, 
  Box, 
  Button, 
  CircularProgress, 
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem, 
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography, 
  useTheme 
} from '@mui/material';
import { useEffect, useState } from 'react';
import { IUserSettings } from '../../../Models/IUserSettings';
import { ApiService } from '../../../Services/ApiService';
import { setLocalStorageItem, setUserSettings } from '../../../Services/Global';
import SaveIcon from '@mui/icons-material/Save';
import logger from '../../../Services/LoggingService';
import { createFlexStyles, createContentStyles } from '../../../theme/layoutStyles';

/**
 * Settings component for user settings page using Material UI
 */
export const Settings: React.FC = () => {
  const ctx: IMsalContext = useMsal();
  const apiService: ApiService = new ApiService(ctx);
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  const contentStyles = createContentStyles(theme);
  
  // State management
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [unitsSetting, setUnitsSetting] = useState('');
  const [currentSettings, setCurrentSettings] = useState<IUserSettings>();

  // Styles
  const styles = {
    container: {
      padding: 3,
      marginTop: 2
    },
    contentWrapper: {
      width: '100%',
      maxWidth: '98%',
      mx: 'auto',
      overflowX: 'visible'
    },
    formContainer: {
      p: 3,
      mt: 2,
      borderRadius: 1,
      boxShadow: theme.shadows[2],
      bgcolor: 'background.paper'
    },
    formControl: {
      m: 1,
      minWidth: 300,
      maxWidth: 500
    },
    buttonContainer: {
      mt: 3,
      width: '100%',
      ...flexStyles.flexCenter
    },
    statusContainer: {
      minHeight: 60,
      mt: 3,
      mb: 2,
      width: '100%',
      ...flexStyles.flexCenter
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 200,
      width: '100%'
    }
  };

  // Fetch user settings on component mount
  useEffect(() => {
    async function getUserSettings() {
      try {
        const settingsResponse = await apiService.getAsync('settings');
        if (settingsResponse.status >= 400) {
          const err = `There was an issue getting User Settings. Status code: ${settingsResponse.status}`;
          setErrorMessage(err);
          throw new Error(err);
        }

        const settings: IUserSettings = await settingsResponse.json();
        setCurrentSettings(settings);
        setUnitsSetting(settings.preferredUnits);
        setUserSettings(settings);
      } catch (error) {
        logger.error('Failed to fetch user settings', error as Error, null, {
          component: 'Settings',
          method: 'getUserSettings'
        });
        setErrorMessage('Failed to load user settings');
      } finally {
        setIsLoading(false);
      }
    }

    getUserSettings();
  }, []);

  /**
   * Handle units dropdown change
   */
  const handleUnitsChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    // Convert 'US' to 'us' to match the original behavior
    setUnitsSetting(selectedValue === 'US' ? 'us' : selectedValue);
  };

  /**
   * Handle saving user settings
   */
  const handleSaveChanges = async () => {
    setHasSaved(false);
    setIsBusy(true);
    
    try {
      const settings: IUserSettings = {
        preferredUnits: unitsSetting
      };
      
      setLocalStorageItem('userSettings', JSON.stringify(settings));
      const response = await apiService.postAsync(settings, 'settings');
      
      if (response.status >= 400) {
        const errorMessage = `An error occurred while saving settings. Status code: ${response.status}`;
        setErrorMessage(errorMessage);
        throw new Error(errorMessage);
      }

      setUserSettings(settings);
      setHasSaved(true);
    } catch (error) {
      logger.error('Failed to save settings', error as Error, {
        preferredUnits: unitsSetting
      }, {
        component: 'Settings',
        method: 'handleSaveChanges'
      });
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Stack spacing={3} sx={styles.contentWrapper}>
        <Box sx={flexStyles.flexCenterBetween}>
        </Box>

        {isLoading ? (
          <Box sx={contentStyles.loadingState}>
            <CircularProgress size={40} />
            <Typography variant="body1" ml={2}>
              Loading settings...
            </Typography>
          </Box>
        ) : (
          <Paper elevation={2} sx={styles.formContainer}>
            <Stack spacing={3} alignItems="center">
              <FormControl sx={styles.formControl}>
                <InputLabel id="units-select-label">Display units</InputLabel>
                <Select
                  labelId="units-select-label"
                  id="units-select"
                  value={unitsSetting === 'us' ? 'US' : unitsSetting}
                  label="Display units"
                  onChange={handleUnitsChange}
                  disabled={isBusy}
                  fullWidth
                >
                  <MenuItem value="metric">Metric</MenuItem>
                  <MenuItem value="US">US</MenuItem>
                </Select>
                <FormHelperText>
                  Sets default units to metric (celsius/kgs) or US (fahrenheit/lbs).
                </FormHelperText>
              </FormControl>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveChanges}
                disabled={isBusy}
                sx={{ 
                  whiteSpace: 'nowrap', 
                  padding: '6px 26px !important',
                  textTransform: 'none',
                  boxShadow: 1
                }}
              >
                Save Changes
              </Button>
            
              {isBusy && (
                <Box sx={flexStyles.flexCenter}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" ml={1}>Saving...</Typography>
                </Box>
              )}
              
              {hasSaved && (
                <Alert severity="success">
                  Settings saved successfully
                </Alert>
              )}
              
              {errorMessage && (
                <Alert severity="error">
                  {errorMessage}
                </Alert>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}; 